import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  ChefHat, 
  Plus, 
  X, 
  Sparkles,
  ArrowRight,
  Download,
  Printer,
  Trash2
} from 'lucide-react';
import { Recipe } from '../../types';

/**
 * Recipe Generator Tool - Swiss knife functionality
 * Generates recipes based on available ingredients
 */

const ToolContainer = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius['2xl']};
  padding: ${props => props.theme.spacing[8]};
  border: 1px solid ${props => props.theme.colors.border};
  max-width: 800px;
  margin: 0 auto;
`;

const ToolHeader = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing[8]};
`;

const ToolIcon = styled.div`
  width: 60px;
  height: 60px;
  background: ${props => props.theme.gradients.cosmic};
  border-radius: ${props => props.theme.borderRadius.xl};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${props => props.theme.spacing[4]};
  color: white;
`;

const ToolTitle = styled.h3`
  font-size: ${props => props.theme.typography.sizes['2xl']};
  font-weight: ${props => props.theme.typography.weights.bold};
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing[2]};
`;

const ToolDescription = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  line-height: ${props => props.theme.typography.lineHeights.relaxed};
`;

const InputSection = styled.div`
  margin-bottom: ${props => props.theme.spacing[8]};
`;

const InputSectionTitle = styled.h4`
  font-size: ${props => props.theme.typography.sizes.lg};
  font-weight: ${props => props.theme.typography.weights.semibold};
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing[4]};
`;

const IngredientsInput = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing[3]};
  margin-bottom: ${props => props.theme.spacing[4]};
`;

const InputContainer = styled.div`
  flex: 1;
  position: relative;
`;

const IngredientInput = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[4]};
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  font-size: ${props => props.theme.typography.sizes.base};
  transition: border-color ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.smooth};

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary[500]};
  }
`;

const SuggestionsList = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: ${props => props.theme.colors.surface};
  border: 2px solid ${props => props.theme.colors.primary[500]};
  border-top: none;
  border-radius: 0 0 ${props => props.theme.borderRadius.lg} ${props => props.theme.borderRadius.lg};
  max-height: 200px;
  overflow-y: auto;
  z-index: 10;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
`;

const SuggestionItem = styled(motion.div)`
  padding: ${props => props.theme.spacing[2]} ${props => props.theme.spacing[4]};
  cursor: pointer;
  font-size: ${props => props.theme.typography.sizes.sm};
  color: ${props => props.theme.colors.text};
  border-bottom: 1px solid ${props => props.theme.colors.border};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${props => props.theme.colors.primary[50]};
  }
`;

const AddButton = styled(motion.button)`
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[4]};
  background-color: ${props => props.theme.colors.primary[600]};
  color: white;
  border: none;
  border-radius: ${props => props.theme.borderRadius.lg};
  font-weight: ${props => props.theme.typography.weights.medium};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  transition: background-color ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.smooth};

  &:hover {
    background-color: ${props => props.theme.colors.primary[700]};
  }
`;

const IngredientsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing[2]};
`;

const IngredientTag = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  background-color: ${props => props.theme.colors.primary[100]};
  color: ${props => props.theme.colors.primary[700]};
  padding: ${props => props.theme.spacing[2]} ${props => props.theme.spacing[3]};
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${props => props.theme.typography.sizes.sm};
  font-weight: ${props => props.theme.typography.weights.medium};
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.primary[600]};
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;

  &:hover {
    background-color: ${props => props.theme.colors.primary[200]};
  }
`;

const GenerateButton = styled(motion.button)`
  width: 100%;
  padding: ${props => props.theme.spacing[4]} ${props => props.theme.spacing[6]};
  background: ${props => props.theme.gradients.cosmic};
  color: white;
  border: none;
  border-radius: ${props => props.theme.borderRadius.lg};
  font-size: ${props => props.theme.typography.sizes.lg};
  font-weight: ${props => props.theme.typography.weights.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing[3]};
  transition: all ${props => props.theme.animations.duration.normal} ${props => props.theme.animations.easing.smooth};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.lg};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const RecipeCard = styled(motion.div)`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius['2xl']};
  padding: ${props => props.theme.spacing[8]};
  margin-top: ${props => props.theme.spacing[8]};
  box-shadow: ${props => props.theme.shadows.xl};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.theme.gradients.cosmic};
  }
`;

const RecipeHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[4]};
  margin-bottom: ${props => props.theme.spacing[6]};
  padding-bottom: ${props => props.theme.spacing[4]};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const RecipeIcon = styled.div`
  width: 48px;
  height: 48px;
  background: ${props => props.theme.gradients.cosmic};
  border-radius: ${props => props.theme.borderRadius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
`;

const RecipeTitle = styled.h3`
  font-size: ${props => props.theme.typography.sizes['2xl']};
  font-weight: ${props => props.theme.typography.weights.bold};
  color: ${props => props.theme.colors.text};
  margin: 0;
  line-height: ${props => props.theme.typography.lineHeights.tight};
`;

const RecipeDescription = styled.p`
  font-size: ${props => props.theme.typography.sizes.base};
  color: ${props => props.theme.colors.textSecondary};
  line-height: ${props => props.theme.typography.lineHeights.relaxed};
  margin-bottom: ${props => props.theme.spacing[6]};
`;

const RecipeMeta = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: ${props => props.theme.spacing[4]};
  margin-bottom: ${props => props.theme.spacing[8]};
`;

const MetaItem = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing[4]};
  background: ${props => props.theme.colors.primary[50]};
  border: 1px solid ${props => props.theme.colors.primary[200]};
  border-radius: ${props => props.theme.borderRadius.lg};
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.smooth};

  &:hover {
    background: ${props => props.theme.colors.primary[100]};
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.md};
  }
`;

const MetaValue = styled.div`
  font-size: ${props => props.theme.typography.sizes.xl};
  font-weight: ${props => props.theme.typography.weights.bold};
  color: ${props => props.theme.colors.primary[700]};
  margin-bottom: ${props => props.theme.spacing[1]};
`;

const MetaLabel = styled.div`
  font-size: ${props => props.theme.typography.sizes.sm};
  color: ${props => props.theme.colors.primary[600]};
  font-weight: ${props => props.theme.typography.weights.medium};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const RecipeSection = styled.div`
  margin-bottom: ${props => props.theme.spacing[8]};

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h4`
  font-size: ${props => props.theme.typography.sizes.xl};
  font-weight: ${props => props.theme.typography.weights.bold};
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing[4]};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};

  &::before {
    content: '';
    width: 4px;
    height: 20px;
    background: ${props => props.theme.gradients.cosmic};
    border-radius: 2px;
  }
`;

// RecipeIngredients and RecipeInstructions components removed as they're not used

const IngredientsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${props => props.theme.spacing[3]};
`;

const IngredientItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  padding: ${props => props.theme.spacing[3]};
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.smooth};

  &:hover {
    background: ${props => props.theme.colors.primary[50]};
    border-color: ${props => props.theme.colors.primary[300]};
    transform: translateY(-1px);
  }
`;

const IngredientAmount = styled.span`
  font-weight: ${props => props.theme.typography.weights.semibold};
  color: ${props => props.theme.colors.primary[700]};
  font-size: ${props => props.theme.typography.sizes.sm};
  min-width: 60px;
`;

const IngredientName = styled.span`
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.sm};
`;

const InstructionsList = styled.ol`
  list-style: none;
  counter-reset: step-counter;
  padding: 0;
  margin: 0;
`;

const InstructionItem = styled.li`
  counter-increment: step-counter;
  margin-bottom: ${props => props.theme.spacing[4]};
  margin-left: ${props => props.theme.spacing[8]};
  padding: ${props => props.theme.spacing[4]};
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  position: relative;
  line-height: ${props => props.theme.typography.lineHeights.relaxed};
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.smooth};

  &:hover {
    background: ${props => props.theme.colors.primary[50]};
    border-color: ${props => props.theme.colors.primary[300]};
    transform: translateX(4px);
  }

  &::before {
    content: counter(step-counter);
    position: absolute;
    left: -24px;
    top: 50%;
    transform: translateY(-50%);
    width: 32px;
    height: 32px;
    background: ${props => props.theme.gradients.cosmic};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: ${props => props.theme.typography.sizes.sm};
    font-weight: ${props => props.theme.typography.weights.bold};
    color: white;
    box-shadow: ${props => props.theme.shadows.md};
  }
`;

const RecipeActions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing[3]};
  margin-top: ${props => props.theme.spacing[6]};
  padding-top: ${props => props.theme.spacing[4]};
  border-top: 1px solid ${props => props.theme.colors.border};
  justify-content: flex-end;
`;

const ActionButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[4]};
  background: ${props => props.theme.colors.primary[600]};
  color: white;
  border: none;
  border-radius: ${props => props.theme.borderRadius.lg};
  font-size: ${props => props.theme.typography.sizes.sm};
  font-weight: ${props => props.theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.smooth};

  &:hover {
    background: ${props => props.theme.colors.primary[700]};
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.md};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const ClearButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[4]};
  background: #dc2626;
  color: white;
  border: none;
  border-radius: ${props => props.theme.borderRadius.lg};
  font-size: ${props => props.theme.typography.sizes.sm};
  font-weight: ${props => props.theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.smooth};

  &:hover {
    background: #b91c1c;
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.md};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;


const RecipeGenerator: React.FC = () => {
  const [ingredients, setIngredients] = useState<string[]>([]);
  const [newIngredient, setNewIngredient] = useState('');
  const [generatedRecipe, setGeneratedRecipe] = useState<Recipe | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Fetch ingredient suggestions
  const fetchSuggestions = async (query: string) => {
    if (query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      const response = await fetch(`https://recipe-generator-production-c08e.up.railway.app/api/ingredients?q=${encodeURIComponent(query)}`);
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data);
        setShowSuggestions(data.length > 0);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  };

  const addIngredient = () => {
    if (newIngredient.trim() && !ingredients.includes(newIngredient.trim())) {
      setIngredients([...ingredients, newIngredient.trim()]);
      setNewIngredient('');
      setShowSuggestions(false);
    }
  };

  const selectSuggestion = (suggestion: string) => {
    setNewIngredient(suggestion);
    setShowSuggestions(false);
  };

  const clearRecipe = () => {
    setGeneratedRecipe(null);
    setIngredients([]);
    setNewIngredient('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const removeIngredient = (ingredient: string) => {
    setIngredients(ingredients.filter(ing => ing !== ingredient));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (showSuggestions && suggestions.length > 0) {
        selectSuggestion(suggestions[0]);
      } else {
        addIngredient();
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewIngredient(value);
    fetchSuggestions(value);
  };

  const downloadRecipe = () => {
    if (!generatedRecipe) return;

    const recipeText = `
${generatedRecipe.name}
${'='.repeat(generatedRecipe.name.length)}

${generatedRecipe.description}

PREPARATION TIME: ${generatedRecipe.prepTime} minutes
COOKING TIME: ${generatedRecipe.cookTime} minutes
SERVINGS: ${generatedRecipe.servings}
DIFFICULTY: ${generatedRecipe.difficulty.toUpperCase()}

INGREDIENTS:
${generatedRecipe.ingredients.map(ing => `• ${ing.amount} ${ing.name}`).join('\n')}

INSTRUCTIONS:
${generatedRecipe.instructions.map((instruction, index) => `${index + 1}. ${instruction}`).join('\n\n')}

---
Generated by Recipe Generator - Portfolio Nadir
Generated on: ${new Date().toLocaleDateString()}
    `.trim();

    const blob = new Blob([recipeText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${generatedRecipe.name.replace(/[^a-zA-Z0-9]/g, '_')}_recipe.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const printRecipe = () => {
    if (!generatedRecipe) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${generatedRecipe.name}</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              line-height: 1.6;
              max-width: 800px;
              margin: 0 auto;
              padding: 20px;
              color: #333;
            }
            h1 {
              color: #2563eb;
              border-bottom: 3px solid #2563eb;
              padding-bottom: 10px;
            }
            .meta {
              background: #f8fafc;
              padding: 15px;
              border-radius: 8px;
              margin: 20px 0;
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
              gap: 10px;
            }
            .meta-item {
              text-align: center;
            }
            .meta-value {
              font-weight: bold;
              color: #2563eb;
            }
            .meta-label {
              font-size: 0.9em;
              color: #64748b;
              text-transform: uppercase;
            }
            .section {
              margin: 30px 0;
            }
            .section h2 {
              color: #2563eb;
              border-left: 4px solid #2563eb;
              padding-left: 15px;
            }
            .ingredients {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
              gap: 10px;
            }
            .ingredient {
              padding: 10px;
              background: #f8fafc;
              border-radius: 6px;
              border-left: 3px solid #2563eb;
            }
            .ingredient-amount {
              font-weight: bold;
              color: #2563eb;
            }
            .instructions {
              counter-reset: step;
            }
            .instruction {
              counter-increment: step;
              margin: 15px 0;
              padding: 15px;
              background: #f8fafc;
              border-radius: 8px;
              position: relative;
              padding-left: 50px;
            }
            .instruction::before {
              content: counter(step);
              position: absolute;
              left: 15px;
              top: 15px;
              width: 25px;
              height: 25px;
              background: #2563eb;
              color: white;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              font-weight: bold;
              font-size: 0.9em;
            }
            .footer {
              margin-top: 40px;
              padding-top: 20px;
              border-top: 1px solid #e2e8f0;
              text-align: center;
              color: #64748b;
              font-size: 0.9em;
            }
            @media print {
              body { margin: 0; padding: 15px; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <h1>${generatedRecipe.name}</h1>
          <p>${generatedRecipe.description}</p>
          
          <div class="meta">
            <div class="meta-item">
              <div class="meta-value">${generatedRecipe.prepTime} min</div>
              <div class="meta-label">Prep Time</div>
            </div>
            <div class="meta-item">
              <div class="meta-value">${generatedRecipe.cookTime} min</div>
              <div class="meta-label">Cook Time</div>
            </div>
            <div class="meta-item">
              <div class="meta-value">${generatedRecipe.servings}</div>
              <div class="meta-label">Servings</div>
            </div>
            <div class="meta-item">
              <div class="meta-value">${generatedRecipe.difficulty}</div>
              <div class="meta-label">Difficulty</div>
            </div>
          </div>

          <div class="section">
            <h2>Ingredients</h2>
            <div class="ingredients">
              ${generatedRecipe.ingredients.map(ing => `
                <div class="ingredient">
                  <span class="ingredient-amount">${ing.amount}</span> ${ing.name}
                </div>
              `).join('')}
            </div>
          </div>

          <div class="section">
            <h2>Instructions</h2>
            <div class="instructions">
              ${generatedRecipe.instructions.map(instruction => `
                <div class="instruction">${instruction}</div>
              `).join('')}
            </div>
          </div>

          <div class="footer">
            Generated by Recipe Generator - Portfolio Nadir<br>
            Generated on: ${new Date().toLocaleDateString()}
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const generateRecipe = async () => {
    if (ingredients.length === 0) return;

    setIsGenerating(true);
    
    try {
      const response = await fetch('https://recipe-generator-production-c08e.up.railway.app/api/generate-recipe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ingredients }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate recipe');
      }

      const recipe = await response.json();
      setGeneratedRecipe(recipe);
    } catch (error) {
      console.error('Error generating recipe:', error);
      // Fallback to mock recipe if API fails
      const mockRecipe: Recipe = {
        id: '1',
        name: `${ingredients[0]} Delight`,
        description: `A delicious recipe created with your available ingredients: ${ingredients.join(', ')}.`,
        ingredients: ingredients.map((ing, index) => ({
          id: index.toString(),
          name: ing,
          category: 'other' as const,
          amount: Math.floor(Math.random() * 3) + 1 + ' cup(s)'
        })),
        instructions: [
          `Prepare all your ingredients: ${ingredients.join(', ')}.`,
          'Heat a large pan over medium heat.',
          'Add your main ingredients and cook for 10-15 minutes.',
          'Season to taste and serve hot.',
          'Enjoy your delicious meal!'
        ],
        prepTime: Math.floor(Math.random() * 20) + 10,
        cookTime: Math.floor(Math.random() * 30) + 15,
        servings: Math.floor(Math.random() * 4) + 2,
        difficulty: ['easy', 'medium', 'hard'][Math.floor(Math.random() * 3)] as 'easy' | 'medium' | 'hard',
        tags: ['quick', 'delicious', 'homemade']
      };
      setGeneratedRecipe(mockRecipe);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <ToolContainer>
      <ToolHeader>
        <ToolIcon>
          <ChefHat size={32} />
        </ToolIcon>
        <ToolTitle>Recipe Generator</ToolTitle>
        <ToolDescription>
          Enter your available ingredients and I'll generate a delicious recipe for you!
        </ToolDescription>
      </ToolHeader>

      <InputSection>
        <InputSectionTitle>Available Ingredients</InputSectionTitle>
        <IngredientsInput>
          <InputContainer>
            <IngredientInput
              type="text"
              placeholder="Add an ingredient (e.g., chicken, tomatoes, rice...)"
              value={newIngredient}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              onFocus={() => newIngredient.length >= 2 && setShowSuggestions(true)}
            />
            {showSuggestions && suggestions.length > 0 && (
              <SuggestionsList>
                {suggestions.map((suggestion, index) => (
                  <SuggestionItem
                    key={index}
                    onClick={() => selectSuggestion(suggestion)}
                    whileHover={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                  >
                    {suggestion}
                  </SuggestionItem>
                ))}
              </SuggestionsList>
            )}
          </InputContainer>
          <AddButton
            onClick={addIngredient}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Plus size={16} />
            Add
          </AddButton>
        </IngredientsInput>

        {ingredients.length > 0 && (
          <IngredientsList>
            {ingredients.map((ingredient, index) => (
              <IngredientTag
                key={index}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0 }}
                transition={{ duration: 0.2 }}
              >
                {ingredient}
                <RemoveButton onClick={() => removeIngredient(ingredient)}>
                  <X size={12} />
                </RemoveButton>
              </IngredientTag>
            ))}
          </IngredientsList>
        )}
      </InputSection>

      <GenerateButton
        onClick={generateRecipe}
        disabled={ingredients.length === 0 || isGenerating}
        whileHover={{ scale: ingredients.length > 0 && !isGenerating ? 1.02 : 1 }}
        whileTap={{ scale: ingredients.length > 0 && !isGenerating ? 0.98 : 1 }}
      >
        {isGenerating ? (
          <>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            >
              <Sparkles size={20} />
            </motion.div>
            Generating Recipe...
          </>
        ) : (
          <>
            <ChefHat size={20} />
            Generate Recipe
            <ArrowRight size={20} />
          </>
        )}
      </GenerateButton>

      {generatedRecipe && (
        <RecipeCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <RecipeHeader>
            <RecipeIcon>
              <ChefHat size={24} />
            </RecipeIcon>
            <RecipeTitle>{generatedRecipe.name}</RecipeTitle>
          </RecipeHeader>

          <RecipeDescription>
            {generatedRecipe.description}
          </RecipeDescription>

          <RecipeMeta>
            <MetaItem>
              <MetaValue>{generatedRecipe.prepTime}min</MetaValue>
              <MetaLabel>Prep Time</MetaLabel>
            </MetaItem>
            <MetaItem>
              <MetaValue>{generatedRecipe.cookTime}min</MetaValue>
              <MetaLabel>Cook Time</MetaLabel>
            </MetaItem>
            <MetaItem>
              <MetaValue>{generatedRecipe.servings}</MetaValue>
              <MetaLabel>Servings</MetaLabel>
            </MetaItem>
            <MetaItem>
              <MetaValue style={{ textTransform: 'capitalize' }}>
                {generatedRecipe.difficulty}
              </MetaValue>
              <MetaLabel>Difficulty</MetaLabel>
            </MetaItem>
          </RecipeMeta>

          <RecipeSection>
            <SectionTitle>Ingredients</SectionTitle>
            <IngredientsGrid>
              {generatedRecipe.ingredients.map((ingredient, index) => (
                <IngredientItem key={index}>
                  <IngredientAmount>{ingredient.amount}</IngredientAmount>
                  <IngredientName>{ingredient.name}</IngredientName>
                </IngredientItem>
              ))}
            </IngredientsGrid>
          </RecipeSection>

          <RecipeSection>
            <SectionTitle>Instructions</SectionTitle>
            <InstructionsList>
              {generatedRecipe.instructions.map((instruction, index) => (
                <InstructionItem key={index}>
                  {instruction}
                </InstructionItem>
              ))}
            </InstructionsList>
          </RecipeSection>

          <RecipeActions>
            <ClearButton
              onClick={clearRecipe}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Trash2 size={16} />
              Clear
            </ClearButton>
            <ActionButton
              onClick={downloadRecipe}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Download size={16} />
              Download
            </ActionButton>
            <ActionButton
              onClick={printRecipe}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Printer size={16} />
              Print
            </ActionButton>
          </RecipeActions>
        </RecipeCard>
      )}
    </ToolContainer>
  );
};

export default RecipeGenerator;
